const imageCache = new Map();
let cachedLogoPromise = null;

export const loadLogoBase64 = () => {
    if (cachedLogoPromise) {
        return cachedLogoPromise;
    }

    cachedLogoPromise = loadImageBase64('/kinderland-logo.png').then(res => {
        if (res && res.base64) {
            return res;
        }
        return loadImageBase64('/Logo.png');
    });

    return cachedLogoPromise;
};

export const loadImageBase64 = (url) => {
    if (!url || typeof url !== 'string' || !url.trim()) {
        return Promise.resolve(null);
    }

    const cleanUrl = url.trim();

    if (imageCache.has(cleanUrl)) {
        return imageCache.get(cleanUrl);
    }

    const loadPromise = new Promise(async (resolve) => {
        let loadUrl = cleanUrl;

        // If raw base64 data without data-uri prefix, prepend it
        if (!loadUrl.startsWith('data:') && !loadUrl.startsWith('http') && !loadUrl.startsWith('/') && loadUrl.length > 50) {
            loadUrl = 'data:image/png;base64,' + loadUrl;
        }

        const trimImageCanvas = (canvas) => {
            try {
                const ctx = canvas.getContext('2d');
                const width = canvas.width;
                const height = canvas.height;
                const imgData = ctx.getImageData(0, 0, width, height);
                const data = imgData.data;

                let minX = width, minY = height, maxX = 0, maxY = 0;
                let found = false;

                const step = 2;
                for (let y = 0; y < height; y += step) {
                    for (let x = 0; x < width; x += step) {
                        const idx = (y * width + x) * 4;
                        const r = data[idx];
                        const g = data[idx + 1];
                        const b = data[idx + 2];
                        const a = data[idx + 3];

                        if (a > 15 && !(r > 240 && g > 240 && b > 240)) {
                            if (x < minX) minX = x;
                            if (x > maxX) maxX = x;
                            if (y < minY) minY = y;
                            if (y > maxY) maxY = y;
                            found = true;
                        }
                    }
                }

                if (!found || minX >= maxX || minY >= maxY) {
                    return canvas;
                }

                const pad = 2;
                const cropMinX = Math.max(0, minX - pad);
                const cropMinY = Math.max(0, minY - pad);
                const cropWidth = Math.min(width - cropMinX, (maxX - minX + 1) + pad * 2);
                const cropHeight = Math.min(height - cropMinY, (maxY - minY + 1) + pad * 2);

                const croppedCanvas = document.createElement('canvas');
                croppedCanvas.width = cropWidth;
                croppedCanvas.height = cropHeight;
                const croppedCtx = croppedCanvas.getContext('2d');
                croppedCtx.drawImage(canvas, cropMinX, cropMinY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
                return croppedCanvas;
            } catch (e) {
                return canvas;
            }
        };

        const processDataUrl = (dataUrl) => {
            return new Promise((res) => {
                const img = new Image();
                img.onload = () => {
                    try {
                        let canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);

                        canvas = trimImageCanvas(canvas);

                        res({
                            base64: canvas.toDataURL('image/png'),
                            width: canvas.width,
                            height: canvas.height
                        });
                    } catch (e) {
                        res(null);
                    }
                };
                img.onerror = () => {
                    res(null); // Failed/broken images resolve null
                };
                img.src = dataUrl;
            });
        };

        // 1. Try direct fetch -> blob -> DataURL
        try {
            const response = await fetch(loadUrl);
            if (response.ok) {
                const contentType = response.headers.get('content-type') || '';
                // Ensure response is actually an image (not HTML 404 page)
                if (!contentType || contentType.includes('image') || loadUrl.includes('.jpg') || loadUrl.includes('.png') || loadUrl.includes('.jpeg')) {
                    const blob = await response.blob();
                    if (blob.type.includes('image') || blob.size > 100) {
                        const dataUrl = await new Promise((resBlob) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resBlob(reader.result);
                            reader.onerror = () => resBlob(null);
                            reader.readAsDataURL(blob);
                        });

                        if (dataUrl) {
                            const processed = await processDataUrl(dataUrl);
                            if (processed) {
                                resolve(processed);
                                return;
                            }
                        }
                    }
                }
            }
        } catch (fetchErr) { }

        // 2. Direct data URI or relative path load
        if (loadUrl.startsWith('data:') || loadUrl.startsWith('/')) {
            const processed = await processDataUrl(loadUrl);
            if (processed) {
                resolve(processed);
                return;
            }
        }

        // 3. Fallback proxies only for external remote http(s) URLs (not localhost)
        const isRemoteHttp = (loadUrl.startsWith('http://') || loadUrl.startsWith('https://')) && !loadUrl.includes('localhost');
        if (isRemoteHttp) {
            const weservUrl = 'https://images.weserv.nl/?url=' + encodeURIComponent(loadUrl) + '&w=200&output=png';
            const corsProxy1 = 'https://corsproxy.io/?' + encodeURIComponent(loadUrl);

            try {
                const resWeserv = await fetch(weservUrl);
                if (resWeserv.ok) {
                    const blob = await resWeserv.blob();
                    const reader = new FileReader();
                    const dataUrl = await new Promise(r => {
                        reader.onloadend = () => r(reader.result);
                        reader.readAsDataURL(blob);
                    });
                    if (dataUrl) {
                        const processed = await processDataUrl(dataUrl);
                        if (processed) {
                            resolve(processed);
                            return;
                        }
                    }
                }
            } catch (e) { }

            try {
                const resCors = await fetch(corsProxy1);
                if (resCors.ok) {
                    const blob = await resCors.blob();
                    const reader = new FileReader();
                    const dataUrl = await new Promise(r => {
                        reader.onloadend = () => r(reader.result);
                        reader.readAsDataURL(blob);
                    });
                    if (dataUrl) {
                        const processed = await processDataUrl(dataUrl);
                        if (processed) {
                            resolve(processed);
                            return;
                        }
                    }
                }
            } catch (e) { }
        }

        // Image not available or broken -> resolve null
        resolve(null);
    });

    imageCache.set(cleanUrl, loadPromise);
    return loadPromise;
};
